import { cn } from '../lib/cn';
import { answersLabel, formatDateTime } from '../lib/format';
import { useSyncState } from '../offline/useSync';
import { getSyncer } from '../offline/runtime';

export interface SyncIndicatorProps {
  className?: string;
  /** compact — плашка у шапці; full — картка з поясненням і кнопкою. */
  variant?: 'compact' | 'full';
}

/**
 * Індикатор стану синхронізації.
 *
 * Це не декорація, а обіцянка: і дитина, і батько мають БАЧИТИ правду про те,
 * скільки відповідей ще не доїхало. Ми ніколи не пишемо «збережено на сервері»,
 * поки сервер не підтвердив; поки не підтвердив — чесно пишемо «чекає відправки».
 */
export function SyncIndicator({
  className,
  variant = 'compact',
}: SyncIndicatorProps): React.JSX.Element {
  const state = useSyncState();
  const waiting = state.unsent;

  const tone = state.blockedByAuth
    ? 'auth'
    : state.rejected > 0
      ? 'error'
      : !state.online
        ? 'offline'
        : waiting > 0
          ? 'busy'
          : 'ok';

  const label =
    tone === 'auth'
      ? 'Потрібен вхід'
      : tone === 'error'
        ? `${state.rejected} не прийнято`
        : tone === 'offline'
          ? waiting > 0
            ? `Офлайн · ${waiting} у черзі`
            : 'Офлайн'
          : tone === 'busy'
            ? state.syncing
              ? `Надсилаю ${waiting}…`
              : `${waiting} у черзі`
            : 'Усе збережено';

  const dotColor =
    tone === 'ok'
      ? 'var(--lk-green)'
      : tone === 'busy'
        ? 'var(--lk-gold)'
        : tone === 'offline'
          ? 'var(--lk-cyan)'
          : 'var(--lk-red)';

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={() => void getSyncer().flush()}
        className={cn('lk-chip cursor-pointer', className)}
        title="Натисни, щоб спробувати надіслати зараз"
        aria-label={`Стан збереження: ${label}`}
        data-testid="sync-indicator"
        data-sync-tone={tone}
        data-sync-pending={waiting}
      >
        <span
          className={cn('h-2.5 w-2.5 rounded-full', state.syncing && 'lk-anim-pulse')}
          style={{ background: dotColor }}
          aria-hidden
        />
        <span className="tabular">{label}</span>
      </button>
    );
  }

  return (
    <section
      className={cn('lk-panel-tight p-4', className)}
      aria-label="Стан збереження відповідей"
      data-testid="sync-panel"
      data-sync-pending={waiting}
    >
      <header className="flex items-center gap-2">
        <span
          className={cn('h-3 w-3 rounded-full', state.syncing && 'lk-anim-pulse')}
          style={{ background: dotColor }}
          aria-hidden
        />
        <h3 className="text-base font-black">{label}</h3>
      </header>

      <p className="mt-2 text-sm leading-snug font-semibold text-white/70">
        {waiting > 0
          ? `${answersLabel(waiting)} збережено на пристрої та чекає на відправку. Нічого не загубиться — надішлемо, щойно зʼявиться інтернет.`
          : 'Усі відповіді підтверджені сервером.'}
      </p>

      {state.rejected > 0 ? (
        <div className="mt-2">
          <p className="text-sm font-bold text-[var(--lk-red)]">
            {answersLabel(state.rejected)} сервер не прийняв. Ми їх не видалили.
          </p>
          {/*
            Причина, згрупована за кодом (див. `describeRejections`), а не сирий
            текст помилки. Відколи вирок став поштучним, пачка на двадцять пʼять
            могла б показати двадцять пʼять однакових рядків — замість однієї
            брехні вийшло б багато правд, які лякають більше.
          */}
          {state.lastError ? (
            <p className="mt-1 text-xs font-semibold text-white/55">{state.lastError}</p>
          ) : null}
          {/*
            Без цієї кнопки «не прийнято» було станом без виходу: повернути
            рядки в чергу вміла лише зміна дитини. Найчастіша причина відмови
            минуща — прострочена сесія або чужий профіль на спільному
            планшеті, — тож ручна спроба тут не марна.
          */}
          <button
            type="button"
            className="lk-chip mt-2 cursor-pointer"
            data-testid="retry-rejected"
            onClick={() => void getSyncer().retryRejected()}
          >
            Спробувати ще раз
          </button>
        </div>
      ) : null}

      {state.lastError && state.rejected === 0 ? (
        <p className="mt-2 text-xs font-semibold text-white/50">Остання помилка: {state.lastError}</p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-bold text-white/50">
        <span>
          Останній обмін:{' '}
          <span className="tabular text-white/75">
            {state.lastSyncAt ? formatDateTime(state.lastSyncAt) : 'ще не було'}
          </span>
        </span>
        <button
          type="button"
          className="lk-chip cursor-pointer"
          onClick={() => void getSyncer().flush()}
        >
          Надіслати зараз
        </button>
      </div>
    </section>
  );
}
